import BookIcon from "@ecologi/assets/icons/book.svg";
import LocationIcon from "@ecologi/assets/icons/location_alt.svg";
import {
  getPrimaryProjectLocation,
  getProjectMapUrl,
  getProjectTypeTitle,
  Impact,
  PublicProjectSummary,
  User,
  isTree,
  TileVariant,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import React from "react";
import { ForestModalBase } from "./ForestModalBase";
import { ModalInfoCard } from "./ModalInfoCard";
import { projectSubTypeVisuals, projectTypeVisuals } from "../consts";
import {
  getImpactDate,
  getFundedBy,
  getLocationAndProjectLabel,
  getModalImagePath,
  getTitle,
} from "./utils";
import {
  ProjectContent,
  Title,
  TitleWrapper,
  Text,
  Grid,
  GridIcon,
  StyledLink,
  Bold,
} from "./styles/ForestModal.styles";
import { ImpactFundDisclaimer } from "./ImpactFundDisclaimer";

const ProjectIcon = styled.div`
  display: flex;
  flex-direction: column;
  width: 32px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const ProjectIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    max-width: 70%;
  }
`;

export const ForestImpactModal = ({
  closePopup,
  isOpen,
  isOwnProfile,
  project,
  tileInfo: { impact, isMocked },
  user,
  imageLabel,
}: {
  closePopup: () => void;
  isOpen: boolean;
  isOwnProfile: boolean;
  project: PublicProjectSummary;
  tileInfo: { impact: Impact; isMocked: boolean };
  user: User | null;
  imageLabel?: string;
}) => {
  if (isTree(impact)) return null;

  const title = getTitle(impact);
  const { projectType, projectSubType } = project;
  const projectIcon = projectSubType
    ? projectSubTypeVisuals[projectSubType]
    : projectTypeVisuals[projectType];
  const projectTypeTitle = getProjectTypeTitle(projectType);

  const fundedBy = getFundedBy(user, impact);
  const impactDate = getImpactDate(impact);

  const [location, projectLabel] = getLocationAndProjectLabel(impact, project);
  const projectHref = `/projects/${project.slug}`;
  const projectMapUrl = getProjectMapUrl(getPrimaryProjectLocation(project));

  return (
    <ForestModalBase
      closePopup={closePopup}
      enableSharing={!isMocked}
      imagePath={getModalImagePath(impact, project)}
      imageLabel={imageLabel}
      isOpen={isOpen}
      isPending={impact.isPending}
    >
      <>
        <ProjectContent>
          <ProjectIconWrapper>
            <ProjectIcon>{projectIcon.icon}</ProjectIcon>
            {projectTypeTitle}
          </ProjectIconWrapper>

          <TitleWrapper>
            <Title data-testid="forest-modal-project-title">{title}</Title>
          </TitleWrapper>

          <>
            {fundedBy && (
              <Text data-testid="forest-modal-funded-by">
                <Bold>Funded by:</Bold> {fundedBy}
              </Text>
            )}
            <Text data-testid="forest-modal-date">
              <Bold>{`Date ${impact.isPending ? "ordered" : "funded"}: `}</Bold>
              {impactDate}
            </Text>

            <Grid>
              {location && (
                <>
                  <GridIcon>
                    <LocationIcon />
                  </GridIcon>

                  <Text>
                    {projectMapUrl ? (
                      <StyledLink
                        href={projectMapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`View the project in ${location} (new tab)`}
                      >
                        {location}
                      </StyledLink>
                    ) : (
                      location
                    )}
                  </Text>
                </>
              )}

              {projectLabel && projectHref && (
                <>
                  <GridIcon>
                    <BookIcon />
                  </GridIcon>

                  <Text>
                    <StyledLink
                      href={projectHref}
                      rel="noopener noreferrer"
                      target="_blank"
                      title="View project"
                    >
                      {projectLabel}
                    </StyledLink>
                  </Text>
                </>
              )}
            </Grid>
          </>
        </ProjectContent>

        <ModalInfoCard impact={impact} isOwnProfile={isOwnProfile} />
        {impact.variant === TileVariant.IMPACT_FUND && <ImpactFundDisclaimer />}
      </>
    </ForestModalBase>
  );
};
